/* eslint-disable max-len */
import classNames from 'classnames';
import { autobind } from 'core-decorators';
import { Component, Fragment } from 'react';

import { _ } from '/@/locale';
import { LayoutOption, LayoutReference, LayoutReferences } from '../refuge-options';
import styles from './styles.module.css';

const CONTENTS = [
    {
        type: 'layout-options',
        crops: ['corn'],
        countries: ['BR', 'PY'],
        render: () => (
            <Fragment>
                <p>
                    {_(
                        'El área de refugio se puede diseñar siguiendo diferentes configuraciones. El objetivo es proteger la tecnología Bt sin dañar su función.'
                    )}
                </p>
                <div className={classNames(styles.layoutOptions, styles.columns2)}>
                    <LayoutOption name={_('Bloque')} crop='corn' country='BR' footer={true} image={1}>
                        {_(
                            'Plante un área de refugio en forma de bloque de maíz convencional adyacente al área de maíz Bt.'
                        )}
                    </LayoutOption>
                    <LayoutOption name={_('Perímetro')} crop='corn' country='BR' footer={true} image={2}>
                        {_('Plante un área de refugio en forma de perímetro o de 4 a 6 hileras del lote de maíz Bt.')}
                    </LayoutOption>
                    <LayoutOption name={_('Con otro cultivo')} crop='corn' country='BR' footer={true} image={3}>
                        {_('Plante un área de refugio de maíz convencional hasta 800 m del área de maíz Bt.')}
                    </LayoutOption>
                    <LayoutOption name={_('Franjas')} crop='corn' country='BR' footer={true} image={4}>
                        {_(
                            'Plante un área de refugio de 4 a 6 hileras de maíz convencional dentro del área de maíz Bt.'
                        )}
                    </LayoutOption>
                    <LayoutOption name={_('Pivot Central')} crop='corn' country='BR' footer={true} image={5}>
                        {_(
                            'Plante el refugio en la proporción recomendada por la empresa que produce la semilla dentro del área regada.'
                        )}
                    </LayoutOption>
                </div>
                <div className={styles.layoutReferences}>
                    <p>{_('REFERENCIAS DE COLORES:')}</p>
                    <div>
                        <LayoutReference type='Refuge'>{_('Refugio')}</LayoutReference>
                        <LayoutReference type='WithTechnology'>{_('Maíz bt')}</LayoutReference>
                        <LayoutReference type='OtherCrop'>{_('Otro cultivo')}</LayoutReference>
                    </div>
                </div>
            </Fragment>
        ),
    },
    {
        type: 'layout-options',
        crops: ['corn'],
        countries: ['AR', 'UY'],
        render: () => (
            <Fragment>
                <p>
                    {_(
                        'Para las tecnologías Bt, el refugio consiste en sembrar el 10% del lote con tecnologías no Bt (puede ser tolerante a herbicidas) con ciclo de madurez similar a la del maíz Bt y similar fecha de siembra.'
                    )}
                </p>
                <br />
                <div className={classNames(styles.layoutOptions, styles.columns2)}>
                    <LayoutOption crop='corn' country='AR' footer={true} image={1}>
                        {_('Menos de 1,500 metros de largo.')}
                    </LayoutOption>
                    <LayoutOption crop='corn' country='AR' footer={true} image={2}>
                        {_('Más de 1,500 metros de largo.')}
                    </LayoutOption>
                    <LayoutOption crop='corn' country='AR' footer={true} image={3}>
                        {_('En todas las cabeceras del lote.')}
                    </LayoutOption>
                    <LayoutOption crop='corn' country='AR' footer={true} image={4}>
                        {_('En una esquina de un pivot de riego.')}
                    </LayoutOption>
                </div>
            </Fragment>
        ),
    },
    {
        type: 'rib',
        crops: ['corn-rib'],
        countries: ['AR', 'UY'],
        render: () => (
            <Fragment>
                <div className={styles.rib}>
                    <div className={styles.content}>
                        <p>
                            {_(
                                'El refugio en la bolsa consiste en la mezcla física de semillas del mismo calibre, con y sin la tecnología que controla insectos (Bt) en una proporción 90% / 10% respectivamente en la misma bolsa, para facilitar la operatoria de la siembra y asegurar una distribución homogénea de ambas semillas en el lote. En la mezcla, ambos híbridos elegidos tienen una fenología similar para evitar competencia intra específica y obtener un cultivo uniforme y bien logrado.'
                            )}
                        </p>
                        <p>
                            {_(
                                'RIB cumple, para algunas especies, la misma función que el refugio estructurado, es decir, ambas son herramientas dentro de un programa de MIP, que tienen como objetivo mantener una baja tasa de aparición de individuos resistentes.'
                            )}
                        </p>
                        <p>
                            {_('RIB no debe implementarse en regiones donde la presión de')}{' '}
                            <i>{_('Spodoptera frugiperda')}</i>{' '}
                            {_(
                                'sea alta, porque no funciona como refugio para esta especie debido a su gran movilidad, que le permite trasladarse entre plantas refugio y no refugio. Solamente se recomienda RIB en zonas donde Diatraea saccharalis sea plaga principal (que cumple su ciclo en la misma planta) y haya baja presión de'
                            )}{' '}
                            <i>{_('Spodoptera frugiperda')}</i>.
                        </p>
                    </div>
                    <div className={styles.layoutOption}>
                        <div>
                            <LayoutReferences
                                items={[
                                    { type: 'Rib', name: _('Maíz Bt') },
                                    { type: 'NotRib', name: _('Refugio') },
                                ]}
                            />
                        </div>
                        <div className={styles.option}>
                            <LayoutOption crop='corn' country='AR' image={5} />
                        </div>
                    </div>
                </div>
            </Fragment>
        ),
    },
    {
        type: 'layout-options',
        crops: ['soy'],
        countries: ['AR', 'UY'],
        render: () => (
            <Fragment>
                <p>
                    {_(
                        'Como la soja es un cultivo autógamo y frecuentemente se reserva parte de la cosecha como semilla, la disposición del refugio deberá considerar las herramientas de siembra, control de plagas y cosecha con las que cuenta el productor para preservar la pureza y calidad de la cosecha.'
                    )}
                </p>
                <div className={classNames(styles.layoutOptions, styles.columns3)}>
                    <LayoutOption crop='soy' country='AR' footer={true} image={1}>
                        {_('En todas las cabeceras del lote.')}
                    </LayoutOption>
                    <LayoutOption crop='soy' country='AR' footer={true} image={2}>
                        {_('A través del lote.')}
                    </LayoutOption>
                    <LayoutOption crop='soy' country='AR' footer={true} image={3}>
                        {_('En otro lote del mismo campo.')}
                    </LayoutOption>
                    <LayoutOption crop='soy' country='AR' footer={true} image={4}>
                        {_('En la esquina del lote.')}
                    </LayoutOption>
                    <LayoutOption crop='soy' country='AR' footer={true} image={5}>
                        {_('En una cabecera del lote.')}
                    </LayoutOption>
                    <LayoutOption crop='soy' country='AR' footer={true} image={6}>
                        {_('En una esquina de un pivot de riego.')}
                    </LayoutOption>
                </div>
                <div className={styles.layoutReferences}>
                    <p>{_('Referencias')}</p>
                    <div>
                        <LayoutReference type='Refuge'>{_('Refugio')}</LayoutReference>
                        <LayoutReference type='WithTechnology'>{_('Soja Bt')}</LayoutReference>
                        <LayoutReference type='Line'>{_('1,200 metros')}</LayoutReference>
                    </div>
                </div>
            </Fragment>
        ),
    },
    {
        type: 'layout-options',
        crops: ['soy'],
        countries: ['BR', 'PY'],
        render: () => (
            <Fragment>
                <p>
                    {_(
                        'A área de refúgio pode ser elaborada seguindo diversas configurações. O objetivo é proteger a tecnologia Bt sem prejudicar o seu negócio.'
                    )}
                </p>
                <div className={classNames(styles.layoutOptions, styles.columns2)}>
                    <LayoutOption name='Perímetro' crop='soy' country='BR' footer={true} image={1}>
                        {_('A área de Refúgio é disposta ao redor da área plantada com INTACTA RR2 PRO®.')}
                    </LayoutOption>
                    <LayoutOption name='Bloco' crop='soy' country='BR' footer={true} image={2}>
                        {_(
                            'Nesta opção, as áreas de Refúgio e de INTACTA RR2 PRO® ficam dispostas em blocos lado a lado, com uma distância máxima de 800 metros entre elas.'
                        )}
                    </LayoutOption>
                    <LayoutOption name='Faixas' crop='soy' country='BR' footer={true} image={3}>
                        {_(
                            'Nesta modalidade, as diferentes áreas são intercaladas por faixas, com uma distância máxima de 800 metros entre cada área.'
                        )}
                    </LayoutOption>
                    <LayoutOption name='Talhão' crop='soy' country='BR' footer={true} image={4}>
                        {_(
                            'Nesta opção você escolhe um ou mais talhões que estejam a, no máximo, 800 metros dos outros talhões plantados com INTACTA RR2 PRO®.'
                        )}
                    </LayoutOption>
                </div>
            </Fragment>
        ),
    },
];

@autobind
export default class RefugeLayoutOptions extends Component {
    render() {
        const { country, crops } = this.props;
        const layout = CONTENTS.find((content) => content.crops.includes(crops) && content.countries.includes(country));

        if (!layout) return null;

        return <div>{layout.render()}</div>;
    }
}
